import { Heart, X } from "lucide-react";

import { cn, isClosed } from "../lib/util.js";

/* 상태별 색 — 원의 채움과 꼬리 삼각형이 같은 색이어야 한 덩어리로 읽힌다.
   지도 위에서는 리스트보다 대비가 더 필요하다. 배경이 지도 타일이라
   미방문의 옅은 테두리(--input)가 묻혀서, 여기서만 한 단계 진한 색을 쓴다.

   closed(휴무·영업 종료)는 리뷰에서 나온 상태가 아니라 가게 쪽 사정이다. 그래서 채운 원(판정)이
   아니라 찜처럼 빈 원 + 아이콘으로 그린다 — 색은 목록 배지(ClosedMark)와 같은 --destructive.
   --never 와 같은 값이지만 속이 비고 ✕ 가 있어 '안 갈 집'과 헷갈리지 않는다. */
const DOT = {
  unvisited: "border-2 border-muted-foreground bg-background",
  want: "border-2 border-dashed border-primary bg-background text-primary",
  again: "bg-again text-white",
  ok: "bg-ok text-white",
  never: "bg-never text-white",
  closed: "border-2 border-destructive bg-background text-destructive",
};
const TAIL = {
  unvisited: "border-t-muted-foreground",
  want: "border-t-primary",
  again: "border-t-again",
  ok: "border-t-ok",
  never: "border-t-never",
  closed: "border-t-destructive",
};

/* 지도 위 annotation 핀 — 원 + 꼬리. 꼬리를 달아 좌표를 정확히 가리키게 한다.
   원만 있으면 중심이 어디인지 애매하다.

   stacked 는 이 핀이 대표하는 곳의 수다(useClusters.js). **원 안의 숫자가 그 수다.**
   겹쳐서 접힌 핀도 평범한 마커로 그린다 — 색은 대표(리뷰가 가장 많은 집)의 것을 쓴다.

   전에는 이 자리에 대표의 방문 횟수를 적었는데, 그러면 지도가 한 자리에서 두 가지를
   말하게 된다: '여기 몇 곳'과 '몇 번 가봤나'. 게다가 접힌 핀의 횟수는 대표 한 집의
   것이라, 같이 접힌 나머지는 세지도 않는 수였다.
   리뷰가 있고 없고는 색이 말한다(DOT). 숫자는 개수만 말한다. */
export function Marker({ store, status, selected, rolling, winner,
                         stacked = 1, onSelect }) {
  /* 한 곳뿐이면 비운다 — 모든 핀에 '1' 을 적으면 지도가 1 로 뒤덮이는데, 한 곳이라는
     것은 뒤의 두 번째 원이 없다는 것으로 이미 읽힌다.

     찜은 아이콘이다. 하트를 '♥' 글리프로 찍지 않는다 — 앱의 다른 하트(말풍선·카드·시트·칩)는
     전부 lucide 아이콘이라 모양이 미묘하게 다른 하트가 두 종류 생긴다. 글꼴 글리프라
     선폭·비율도 Pretendard 를 따라가고, 텍스트 줄상자(ascender/descender)에 갇혀서 원 안에서
     광학적으로 위로 붕 뜬다. SVG 는 줄상자가 없어 place-items-center 로 정확히 앉는다.

     접힌 핀에서는 숫자가 하트를 이긴다. 하트는 대표 한 집의 상태일 뿐이라, 그 자리에
     몇 곳이 있는지보다 급하지 않다. */
  const label = stacked > 1 ? String(stacked) : "";
  /* 휴무는 핀을 누르기 전에 보여야 한다 — 가려고 누른 뒤에야 알면 늦다.
     휴무가 리뷰 상태를 이긴다. 찜했든 가봤든 오늘은 못 가는 집이다.
     접힌 핀에는 안 쓴다: 대표 한 집이 닫았다고 같이 접힌 나머지까지 닫힌 것처럼
     보이면 안 된다. 그 경우는 펼친 목록의 배지가 말한다. */
  const look = isClosed(store) && stacked === 1 ? "closed" : status;
  return (
    <div
      className={cn(
        "map-mk relative flex cursor-pointer flex-col items-center",
        stacked > 1 && "map-cl",
        selected && "sel",
        rolling && "roll",
        /* 당첨 — 마커에서 파동이 두 번 퍼진다 */
        winner && "win before:pointer-events-none before:absolute before:top-3 before:left-1/2 " +
                  "before:-mt-3 before:-ml-3 before:size-6 before:animate-ripple before:rounded-full " +
                  "before:border-2 before:border-primary before:content-['']",
      )}
      data-state={status}
      data-closed={look === "closed" || undefined}
      data-id={store.id}
      data-n={stacked > 1 ? stacked : undefined}
      /* 지도를 옮길지 말지는 여기서 안 정한다 — 화면 폭에 달린 일이라 App 의 pickPin 몫이다.
         마커는 '이게 눌렸다'만 알린다. */
      onClick={(e) => { e.stopPropagation(); onSelect(store.id); }}
    >
      {/* 뒤에 반 칸 밀려 나온 두 번째 원 — 이 자리에 더 있다는 표시.
          대표와 같은 색을 쓴다. 여기서만 다른 색을 쓰면 '겹침'이 판정 상태의 하나처럼
          읽힌다. 흰 테로 앞의 원과 갈라놔야 두 겹인 게 보인다.
          형제로 두고 앞의 원에 relative 를 준다 — 둘 다 위치를 잡은 요소가 되면 뒤에
          쓴 쪽이 위로 올라온다. ::before 로 하면 z-index 를 음수로 줘도 부모의 배경보다
          아래로는 못 내려가서 대표 원을 덮는다. */}
      {stacked > 1 && (
        <div
          className={cn(
            "stack absolute -top-[3px] left-[3px] box-border size-6 rounded-full",
            "border-2 border-background shadow-[0_1px_5px_rgba(0,0,0,.3)]",
            DOT[look],
          )}
        />
      )}
      <div
        className={cn(
          "mk relative box-border grid size-6 place-items-center rounded-full text-[11px] font-bold",
          "shadow-[0_1px_5px_rgba(0,0,0,.3)]",
          /* 확대는 아래쪽 끝을 축으로 삼는다. 기본값(중심)으로 키우면 원이 아래로도
             자라서 꼬리를 덮는다 — 1.3배에서 꼬리 10px 중 4.4px 만 남았다.
             바닥을 고정하면 원은 위로만 자라고, 꼬리와 물리는 정도는 평소와 같다.
             선택 확대뿐 아니라 룰렛의 mk-pop(1.65배)·mk-win(1.75배)도 같이 해결된다. */
          "origin-bottom",
          DOT[look],
          selected && "scale-[1.3]",
          /* 선택 표시는 3px 실선 링이다. 찜만 링을 씌우지 않는다 — 점선 테두리
             바로 밖에 실선이 한 겹 더 둘리면 점선 사이의 흰 틈이 양쪽에서 파랑에 갇혀
             톱니처럼 보이고, 하트가 톱니바퀴에 박힌 꼴이 된다. 이 마커는 점선이 테두리다.
             선택은 확대(scale)와 길어진 꼬리로 읽힌다. */
          selected && look !== "want" && "shadow-[0_0_0_3px_var(--color-primary)]",
          rolling && "animate-mk-pop",
          winner && "animate-mk-win",
        )}
      >
        {label || (look === "closed"
          ? <X aria-hidden="true" strokeWidth={3.5} className="size-3" />
          : look === "want"
            ? <Heart aria-hidden="true" className="size-3 fill-current" />
            : null)}
      </div>
      {/* 삼각형은 좌우 투명 + 위쪽만 색이 있는 border 로 그린다 */}
      <div
        className={cn(
          "tail -mt-0.5 size-0 border-x-[5px] border-x-transparent border-t-[8px]",
          "[filter:drop-shadow(0_1px_1px_rgba(0,0,0,.25))]",
          TAIL[look],
          selected && "border-t-[10px]",
        )}
      />
    </div>
  );
}

/* 접힌 목록 한 줄 앞의 상태 점 — 핀 하나가 대표로 보여준 색 말고, 나머지 곳들의
   색을 여기서 돌려준다(ClusterBubble).

   마커의 DOT 를 그대로 쓰지 않는 이유는 10px 때문이다. 찜의 점선 테두리는 이 지름에서
   톱니로 뭉개져 '망가진 원'으로 보이므로 실선으로 바꾼다. 나머지는 같은 값이다. */
const CL_DOT = {
  unvisited: "border-2 border-muted-foreground bg-background",
  want: "border-2 border-primary bg-background",
  again: "bg-again",
  ok: "bg-ok",
  never: "bg-never",
};

export function StatusDot({ status }) {
  return (
    <span
      className={cn("cl-dot mt-[5px] size-2.5 flex-none rounded-full", CL_DOT[status])}
      data-state={status}
    />
  );
}

/** 거리 기준점 — 지오코더가 역산한 캡처 시점 현위치. 화면에서는 그게 곧 회사다.
 *
 *  표식은 마스코트 하나다. 라벨 없이도 가게 핀(24px 원)들 사이에서 혼자 생김새가 달라
 *  '여기가 회사다'로 읽힌다. 크기(96px)와 그림자만으로 구분을 감당한다.
 *  그림자 색은 --brand 다. 로고의 'SEMI' 와 같은 값을 쓰는 자리로, 회사를 가리키는 표식이라
 *  --primary(액센트)가 아니라 브랜드 토큰이 맞다. 회사 색이 바뀌면 이 그림자도 따라간다. */
export function OriginMarker() {
  return (
    <img
      src="/images/mascotte.png"
      alt=""
      draggable="false"
      /* 흰 마스코트가 밝은 지도 타일에 묻힌다. 윤곽을 살리되 검정 그림자는 무거워서,
         브랜드색을 반투명으로 얕게(1px 내림, 3px 번짐) 깔아 발밑에만 파랑이 번지게 한다. */
      /* max-w-none 없으면 가로만 찌그러진다. Tailwind preflight 의 `img{max-width:100%}` 가
         걸리는데, 이 img 를 감싼 건 카카오 CustomOverlay 가 만든 폭 없는 div 라 100% 가
         96px 보다 좁게 풀린다. 세로는 size-24 가 잡고 있어서 가로만 눌린다. */
      className="origin-mk pointer-events-none size-24 max-w-none
                 [filter:drop-shadow(0_1px_3px_color-mix(in_srgb,var(--color-brand)_55%,transparent))]"
    />
  );
}
